// Mock Review Service
// This provides mock data for product reviews and ratings when Firebase is not available

// Generate a random date within the last 3 months
const getRandomDate = () => {
  const now = new Date();
  const threeMonthsAgo = new Date();
  threeMonthsAgo.setMonth(now.getMonth() - 3);
  
  return new Date(
    threeMonthsAgo.getTime() + Math.random() * (now.getTime() - threeMonthsAgo.getTime())
  ).toISOString();
};

// Mock user data
const mockUsers = [
  { id: 'user1', name: 'John Doe', avatar: 'https://i.pravatar.cc/150?img=1' },
  { id: 'user2', name: 'Jane Smith', avatar: 'https://i.pravatar.cc/150?img=5' },
  { id: 'user3', name: 'Michael Johnson', avatar: 'https://i.pravatar.cc/150?img=8' },
  { id: 'user4', name: 'Sarah Williams', avatar: 'https://i.pravatar.cc/150?img=9' },
  { id: 'user5', name: 'David Brown', avatar: 'https://i.pravatar.cc/150?img=12' }
];

// Generate mock reviews for a product
const generateMockReviews = (productId) => {
  // Use productId as seed for deterministic but varied reviews
  const seed = parseInt(productId.replace(/\D/g, '')) || 1;
  const reviewCount = (seed % 5) + 1; // 1-5 reviews
  
  const reviews = [];
  
  for (let i = 0; i < reviewCount; i++) {
    const userIndex = (seed + i) % mockUsers.length;
    const user = mockUsers[userIndex];
    const rating = Math.min(5, Math.max(3, ((seed + i) % 5) + 1)); // 3-5 stars
    
    reviews.push({
      id: `review-${productId}-${i}`,
      productId,
      userId: user.id,
      userName: user.name,
      userAvatar: user.avatar,
      rating,
      title: ['Great product!', 'Highly recommend', 'Good value', 'Works well', 'Very satisfied'][i % 5],
      comment: [
        'This product exceeded my expectations. The quality is excellent and it arrived quickly.',
        'I\'ve been using this for a few weeks now and it works perfectly. Great purchase!',
        'Good product for the price. Does exactly what it says.',
        'Very happy with this purchase. Would buy again.',
        'The product is well-made and durable. Definitely worth the money.'
      ][i % 5],
      helpful: (seed + i) % 10,
      helpfulUsers: [],
      reported: false,
      reportCount: 0,
      createdAt: getRandomDate()
    });
  }
  
  return reviews;
};

// Calculate average rating from reviews
const calculateAverageRating = (reviews) => {
  if (!reviews || reviews.length === 0) {
    return { average: 0, count: 0 };
  }
  
  const totalRating = reviews.reduce((sum, review) => sum + review.rating, 0);
  const average = (totalRating / reviews.length).toFixed(1);
  
  return {
    average: parseFloat(average),
    count: reviews.length
  };
};

// In-memory storage for reviews
const reviewsStorage = {};
const userReviewsMap = {};

// Get all reviews for a product
export const getProductReviews = async (productId) => {
  if (!productId) {
    throw new Error('Product ID is required');
  }

  // Check if we already have reviews for this product
  if (!reviewsStorage[productId]) {
    reviewsStorage[productId] = generateMockReviews(productId);
    
    // Update user reviews map
    reviewsStorage[productId].forEach(review => {
      if (!userReviewsMap[review.userId]) {
        userReviewsMap[review.userId] = {};
      }
      userReviewsMap[review.userId][productId] = review.id;
    });
  }

  return reviewsStorage[productId];
};

// Get average rating for a product
export const getProductRating = async (productId) => {
  if (!productId) {
    throw new Error('Product ID is required');
  }

  // Get reviews first
  const reviews = await getProductReviews(productId);
  
  // Calculate and return rating
  return calculateAverageRating(reviews);
};

// Check if a user has already reviewed a product
export const hasUserReviewed = async (productId, userId) => {
  if (!productId || !userId) {
    return false;
  }

  return !!(userReviewsMap[userId] && userReviewsMap[userId][productId]);
};

// Add a new review
export const addReview = async (productId, userId, reviewData) => {
  if (!productId || !userId) {
    throw new Error('Product ID and User ID are required');
  }

  // Check if user has already reviewed this product
  const hasReviewed = await hasUserReviewed(productId, userId);
  
  if (hasReviewed) {
    throw new Error('You have already reviewed this product');
  }
  
  // Find user data
  const userIndex = parseInt(userId.replace(/\D/g, '')) % mockUsers.length;
  const user = mockUsers[userIndex];
  
  // Create new review
  const newReview = {
    id: `review-${productId}-${Date.now()}`,
    productId,
    userId,
    userName: user.name,
    userAvatar: user.avatar,
    rating: reviewData.rating,
    title: reviewData.title,
    comment: reviewData.comment,
    helpful: 0,
    helpfulUsers: [],
    reported: false,
    reportCount: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };
  
  // Add to storage
  if (!reviewsStorage[productId]) {
    reviewsStorage[productId] = [];
  }
  
  reviewsStorage[productId].push(newReview);
  
  // Update user reviews map
  if (!userReviewsMap[userId]) {
    userReviewsMap[userId] = {};
  }
  userReviewsMap[userId][productId] = newReview.id;
  
  return newReview;
};

// Edit a review
export const editReview = async (reviewId, updatedData) => {
  // Find the review
  let foundReview = null;
  let productId = null;
  
  Object.keys(reviewsStorage).forEach(pid => {
    const reviewIndex = reviewsStorage[pid].findIndex(r => r.id === reviewId);
    if (reviewIndex !== -1) {
      foundReview = reviewsStorage[pid][reviewIndex];
      productId = pid;
    }
  });
  
  if (!foundReview) {
    throw new Error('Review not found');
  }
  
  // Update the review
  const updatedReview = {
    ...foundReview,
    rating: updatedData.rating || foundReview.rating,
    title: updatedData.title || foundReview.title,
    comment: updatedData.comment || foundReview.comment,
    updatedAt: new Date().toISOString()
  };
  
  // Replace in storage
  const reviewIndex = reviewsStorage[productId].findIndex(r => r.id === reviewId);
  reviewsStorage[productId][reviewIndex] = updatedReview;
  
  return updatedReview;
};

// Delete a review
export const deleteReview = async (reviewId) => {
  // Find the review
  let productId = null;
  let userId = null;
  
  Object.keys(reviewsStorage).forEach(pid => {
    const reviewIndex = reviewsStorage[pid].findIndex(r => r.id === reviewId);
    if (reviewIndex !== -1) {
      userId = reviewsStorage[pid][reviewIndex].userId;
      productId = pid;
    }
  });
  
  if (!productId) {
    throw new Error('Review not found');
  }
  
  // Remove from storage
  reviewsStorage[productId] = reviewsStorage[productId].filter(r => r.id !== reviewId);
  
  // Remove from user reviews map
  if (userReviewsMap[userId] && userReviewsMap[userId][productId]) {
    delete userReviewsMap[userId][productId];
  }
  
  return true;
};

// Mark a review as helpful
export const markReviewHelpful = async (reviewId, userId) => {
  // Find the review
  let foundReview = null;
  let productId = null;
  
  Object.keys(reviewsStorage).forEach(pid => {
    const reviewIndex = reviewsStorage[pid].findIndex(r => r.id === reviewId);
    if (reviewIndex !== -1) {
      foundReview = reviewsStorage[pid][reviewIndex];
      productId = pid;
    }
  });
  
  if (!foundReview) {
    throw new Error('Review not found');
  }
  
  // Check if user has already marked this review as helpful
  const helpfulUsers = foundReview.helpfulUsers || [];
  let updatedHelpfulUsers;
  let updatedHelpfulCount;
  
  if (helpfulUsers.includes(userId)) {
    // Remove user from helpfulUsers array
    updatedHelpfulUsers = helpfulUsers.filter(id => id !== userId);
    updatedHelpfulCount = foundReview.helpful - 1;
  } else {
    // Add user to helpfulUsers array
    updatedHelpfulUsers = [...helpfulUsers, userId];
    updatedHelpfulCount = foundReview.helpful + 1;
  }
  
  // Update the review
  const updatedReview = {
    ...foundReview,
    helpful: updatedHelpfulCount,
    helpfulUsers: updatedHelpfulUsers
  };
  
  // Replace in storage
  const reviewIndex = reviewsStorage[productId].findIndex(r => r.id === reviewId);
  reviewsStorage[productId][reviewIndex] = updatedReview;
  
  return updatedReview;
};

// Report a review
export const reportReview = async (reviewId, reason) => {
  // Find the review
  let foundReview = null;
  let productId = null;
  
  Object.keys(reviewsStorage).forEach(pid => {
    const reviewIndex = reviewsStorage[pid].findIndex(r => r.id === reviewId);
    if (reviewIndex !== -1) {
      foundReview = reviewsStorage[pid][reviewIndex];
      productId = pid;
    }
  });
  
  if (!foundReview) {
    throw new Error('Review not found');
  }
  
  // Update the review
  const updatedReview = {
    ...foundReview,
    reported: true,
    reportCount: (foundReview.reportCount || 0) + 1
  };
  
  // Replace in storage
  const reviewIndex = reviewsStorage[productId].findIndex(r => r.id === reviewId);
  reviewsStorage[productId][reviewIndex] = updatedReview;
  
  return true;
};

// Clear review reports
export const clearReviewReports = async (reviewId) => {
  // Find the review
  let foundReview = null;
  let productId = null;
  
  Object.keys(reviewsStorage).forEach(pid => {
    const reviewIndex = reviewsStorage[pid].findIndex(r => r.id === reviewId);
    if (reviewIndex !== -1) {
      foundReview = reviewsStorage[pid][reviewIndex];
      productId = pid;
    }
  });
  
  if (!foundReview) {
    throw new Error('Review not found');
  }
  
  // Update the review
  const updatedReview = {
    ...foundReview,
    reported: false,
    reportCount: 0
  };
  
  // Replace in storage
  const reviewIndex = reviewsStorage[productId].findIndex(r => r.id === reviewId);
  reviewsStorage[productId][reviewIndex] = updatedReview;
  
  return true;
};
